import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import toast from 'react-hot-toast';
import logCollectionApi, {
  LogSource,
  FirewallLog,
  LogStats,
  VendorInfo,
  ParserTypeInfo,
  CreateLogSourceData,
  LogSeverity,
  FirewallVendor,
  ParserType,
} from '../../services/api/log-collection.api';

const SEVERITY_COLORS: Record<LogSeverity, string> = {
  emergency: 'bg-red-600 text-white',
  alert: 'bg-red-500 text-white',
  critical: 'bg-red-400 text-white',
  error: 'bg-orange-500 text-white',
  warning: 'bg-yellow-500 text-black',
  notice: 'bg-blue-400 text-white',
  info: 'bg-blue-300 text-black',
  debug: 'bg-gray-400 text-black',
};

const VENDOR_ICONS: Record<FirewallVendor, string> = {
  cisco: '/icons/cisco.svg',
  fortigate: '/icons/fortinet.svg',
  paloalto: '/icons/paloalto.svg',
  checkpoint: '/icons/checkpoint.svg',
  generic: '/icons/firewall.svg',
};

export default function LogCollectionPage() {
  const { t } = useTranslation(['logCollection', 'common']);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState<LogSource[]>([]);
  const [logs, setLogs] = useState<FirewallLog[]>([]);
  const [stats, setStats] = useState<LogStats[]>([]);
  const [vendors, setVendors] = useState<VendorInfo[]>([]);
  const [parserTypes, setParserTypes] = useState<ParserTypeInfo[]>([]);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'sources' | 'logs' | 'stats'>('sources');
  const [logsPage, setLogsPage] = useState(0);
  const [logsTotal, setLogsTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<LogSeverity[]>([]);
  const [actionFilter, setActionFilter] = useState<string[]>([]);
  const [liveMode, setLiveMode] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const liveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Create source form state
  const [newSource, setNewSource] = useState<CreateLogSourceData>({
    name: '',
    description: '',
    enabled: true,
    sourceType: 'syslog',
    vendor: 'generic',
    parserType: 'auto',
    listenPort: 514,
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    } else if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab, selectedSource, logsPage, searchQuery, severityFilter, actionFilter]);

  // Live mode auto-refresh
  useEffect(() => {
    if (liveMode && activeTab === 'logs') {
      liveIntervalRef.current = setInterval(() => {
        fetchLogs();
      }, 3000); // Refresh every 3 seconds

      return () => {
        if (liveIntervalRef.current) {
          clearInterval(liveIntervalRef.current);
        }
      };
    } else if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
    }
  }, [liveMode, activeTab]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [sourcesRes, vendorsRes] = await Promise.all([
        logCollectionApi.getSources(),
        logCollectionApi.getVendors(),
      ]);

      if (sourcesRes.success) {
        setSources(sourcesRes.data);
      }

      if (vendorsRes.success) {
        setVendors(vendorsRes.data.vendors);
        setParserTypes(vendorsRes.data.parserTypes);
      }
    } catch (error: any) {
      console.error('Failed to fetch initial data:', error);
      toast.error(t('common:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await logCollectionApi.queryLogs({
        sourceId: selectedSource || undefined,
        searchQuery: searchQuery || undefined,
        severity: severityFilter.length > 0 ? severityFilter : undefined,
        action: actionFilter.length > 0 ? actionFilter : undefined,
        limit: liveMode ? 100 : 50,
        offset: liveMode ? 0 : logsPage * 50,
        sortOrder: 'desc',
      });

      if (response.success) {
        setLogs(response.data.logs);
        setLogsTotal(response.data.total);
      }
    } catch (error: any) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await logCollectionApi.getStats(selectedSource || undefined);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateSource = async () => {
    try {
      const response = await logCollectionApi.createSource(newSource);
      if (response.success) {
        toast.success(t('logCollection:sources.createSuccess'));
        setSources([response.data, ...sources]);
        setShowCreateModal(false);
        setNewSource({
          name: '',
          description: '',
          enabled: true,
          sourceType: 'syslog',
          vendor: 'generic',
          parserType: 'auto',
          listenPort: 514,
        });
      }
    } catch (error: any) {
      console.error('Failed to create source:', error);
      toast.error(error.response?.data?.error || t('common:errors.createFailed'));
    }
  };

  const handleToggleSource = async (source: LogSource) => {
    try {
      const response = await logCollectionApi.updateSource(source.id, {
        enabled: !source.enabled,
      });
      if (response.success) {
        setSources(sources.map(s => s.id === source.id ? { ...s, enabled: !s.enabled } : s));
        toast.success(source.enabled ? t('logCollection:sources.disabled') : t('logCollection:sources.enabled'));
      }
    } catch (error: any) {
      console.error('Failed to toggle source:', error);
      toast.error(t('common:errors.updateFailed'));
    }
  };

  const handleDeleteSource = async (source: LogSource) => {
    if (!confirm(t('logCollection:sources.confirmDelete', { name: source.name }))) {
      return;
    }

    try {
      const response = await logCollectionApi.deleteSource(source.id);
      if (response.success) {
        setSources(sources.filter(s => s.id !== source.id));
        toast.success(t('logCollection:sources.deleteSuccess'));
      }
    } catch (error: any) {
      console.error('Failed to delete source:', error);
      toast.error(t('common:errors.deleteFailed'));
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('logCollection:title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('logCollection:subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          {/* Setup Agent button hidden - agent does not exist yet
          <button
            onClick={() => setShowSetupModal(true)}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('logCollection:setup.title')}
          </button>
          */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            {t('logCollection:sources.add')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('logCollection:stats.totalSources')}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                {sources.length}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/20">
              <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('logCollection:stats.activeSources')}
              </p>
              <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
                {sources.filter(s => s.enabled).length}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/20">
              <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('logCollection:stats.totalLogs')}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                {formatNumber(sources.reduce((acc, s) => acc + s.totalLogsReceived, 0))}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20">
              <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t('logCollection:stats.vendors')}
              </p>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                {new Set(sources.map(s => s.vendor)).size}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/20">
              <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('sources')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'sources'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              {t('logCollection:tabs.sources')}
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'logs'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              {t('logCollection:tabs.logs')}
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'stats'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
              }`}
            >
              {t('logCollection:tabs.stats')}
            </button>
          </nav>
        </div>
      </div>

      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div className="space-y-4">
          {sources.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
              </svg>
              <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
                {t('logCollection:sources.noSources')}
              </h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {t('logCollection:sources.noSourcesDescription')}
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                {t('logCollection:sources.add')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sources.map((source) => (
                <div
                  key={source.id}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        source.enabled ? 'bg-green-100 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <svg className={`h-5 w-5 ${source.enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{source.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{source.vendor.toUpperCase()}</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      source.enabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {source.enabled ? t('logCollection:sources.active') : t('logCollection:sources.inactive')}
                    </span>
                  </div>

                  {source.description && (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                      {source.description}
                    </p>
                  )}

                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('logCollection:sources.type')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{source.sourceType}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('logCollection:sources.port')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{source.listenPort || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('logCollection:sources.logsReceived')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{formatNumber(source.totalLogsReceived)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{t('logCollection:sources.lastLog')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {source.lastLogAt ? formatTimestamp(source.lastLogAt) : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => handleToggleSource(source)}
                      className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        source.enabled
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400'
                          : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400'
                      }`}
                    >
                      {source.enabled ? t('logCollection:sources.disable') : t('logCollection:sources.enable')}
                    </button>
                    <button
                      onClick={() => handleDeleteSource(source)}
                      className="rounded-lg bg-red-100 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400"
                    >
                      {t('common:delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Live Mode Banner */}
          {liveMode && (
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="text-sm font-medium text-green-800 dark:text-green-300">
                  {t('logCollection:logs.liveMode')} - {t('logCollection:logs.refreshing')}
                </span>
              </div>
              <button
                onClick={() => setLiveMode(false)}
                className="text-sm text-green-600 dark:text-green-400 hover:underline"
              >
                {t('logCollection:logs.stopLive')}
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex gap-4 flex-wrap items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('logCollection:logs.search')}
                </label>
                <input
                  type="text"
                  placeholder={t('logCollection:logs.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('logCollection:logs.source')}
                </label>
                <select
                  value={selectedSource || ''}
                  onChange={(e) => setSelectedSource(e.target.value || null)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">{t('logCollection:logs.allSources')}</option>
                  {sources.map((source) => (
                    <option key={source.id} value={source.id}>{source.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('logCollection:logs.action')}
                </label>
                <select
                  value={actionFilter[0] || ''}
                  onChange={(e) => setActionFilter(e.target.value ? [e.target.value] : [])}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">{t('logCollection:logs.allActions')}</option>
                  <option value="allow">Allow</option>
                  <option value="deny">Deny</option>
                  <option value="drop">Drop</option>
                  <option value="reject">Reject</option>
                </select>
              </div>
              <button
                onClick={() => setLiveMode(!liveMode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  liveMode
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  {liveMode ? (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                      {t('logCollection:logs.stopLive')}
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('logCollection:logs.startLive')}
                    </>
                  )}
                </span>
              </button>
            </div>

            {/* Severity Quick Filters */}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 self-center mr-1">
                {t('logCollection:logs.severity')}:
              </span>
              {(['emergency', 'alert', 'critical', 'error', 'warning', 'notice', 'info', 'debug'] as LogSeverity[]).map((severity) => (
                <button
                  key={severity}
                  onClick={() => {
                    if (severityFilter.includes(severity)) {
                      setSeverityFilter(severityFilter.filter(s => s !== severity));
                    } else {
                      setSeverityFilter([...severityFilter, severity]);
                    }
                  }}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                    severityFilter.includes(severity)
                      ? SEVERITY_COLORS[severity]
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {severity.charAt(0).toUpperCase() + severity.slice(1)}
                </button>
              ))}
              {severityFilter.length > 0 && (
                <button
                  onClick={() => setSeverityFilter([])}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  {t('common:clearAll')}
                </button>
              )}
            </div>
          </div>

          {/* Logs Table */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.timestamp')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.severity')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.source')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.action')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.srcDst')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('logCollection:logs.message')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        {t('logCollection:logs.noLogs')}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${SEVERITY_COLORS[log.severity]}`}>
                            {log.severity.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {log.sourceIp}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.action === 'allow' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            log.action === 'deny' || log.action === 'drop' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {log.action || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {log.srcIp && log.dstIp ? `${log.srcIp}:${log.srcPort || '*'} → ${log.dstIp}:${log.dstPort || '*'}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-md truncate">
                          {log.parsedMessage || log.rawMessage}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {logsTotal > 50 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('logCollection:logs.showing', {
                    start: logsPage * 50 + 1,
                    end: Math.min((logsPage + 1) * 50, logsTotal),
                    total: logsTotal
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLogsPage(p => Math.max(0, p - 1))}
                    disabled={logsPage === 0}
                    className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                  >
                    {t('common:previous')}
                  </button>
                  <button
                    onClick={() => setLogsPage(p => p + 1)}
                    disabled={(logsPage + 1) * 50 >= logsTotal}
                    className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 disabled:opacity-50"
                  >
                    {t('common:next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {stats.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
              <p className="text-gray-600 dark:text-gray-400">{t('logCollection:stats.noStats')}</p>
            </div>
          ) : (
            stats.map((stat) => {
              const source = sources.find(s => s.id === stat.sourceId);
              return (
                <div
                  key={stat.sourceId}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {source?.name || stat.sourceId}
                  </h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('logCollection:stats.totalLogs')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatNumber(stat.totalLogs)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('logCollection:stats.errors')}</p>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">{formatNumber(stat.errorCount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('logCollection:stats.logsPerSecond')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.logsPerSecond.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('logCollection:stats.lastLog')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {stat.lastLogAt ? formatTimestamp(stat.lastLogAt) : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Severity Breakdown */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('logCollection:stats.severityBreakdown')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(stat.severityBreakdown).map(([severity, count]) => (
                        count > 0 && (
                          <span
                            key={severity}
                            className={`px-3 py-1 rounded-full text-sm font-medium ${SEVERITY_COLORS[severity as LogSeverity]}`}
                          >
                            {severity}: {formatNumber(count)}
                          </span>
                        )
                      ))}
                    </div>
                  </div>

                  {/* Top IPs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('logCollection:stats.topSourceIPs')}
                      </h4>
                      <div className="space-y-2">
                        {stat.topSrcIps.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-900 dark:text-white font-mono">{item.ip}</span>
                            <span className="text-gray-500 dark:text-gray-400">{formatNumber(item.count)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('logCollection:stats.topDestIPs')}
                      </h4>
                      <div className="space-y-2">
                        {stat.topDstIps.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-900 dark:text-white font-mono">{item.ip}</span>
                            <span className="text-gray-500 dark:text-gray-400">{formatNumber(item.count)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create Source Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {t('logCollection:sources.create')}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('logCollection:sources.name')} *
                </label>
                <input
                  type="text"
                  value={newSource.name}
                  onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder={t('logCollection:sources.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('logCollection:sources.description')}
                </label>
                <textarea
                  value={newSource.description}
                  onChange={(e) => setNewSource({ ...newSource, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('logCollection:sources.vendor')}
                  </label>
                  <select
                    value={newSource.vendor}
                    onChange={(e) => setNewSource({ ...newSource, vendor: e.target.value as FirewallVendor })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('logCollection:sources.sourceType')}
                  </label>
                  <select
                    value={newSource.sourceType}
                    onChange={(e) => setNewSource({ ...newSource, sourceType: e.target.value as any })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="syslog">Syslog</option>
                    <option value="snmp">SNMP</option>
                    <option value="api">API</option>
                    <option value="agent">Agent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('logCollection:sources.listenPort')}
                  </label>
                  <input
                    type="number"
                    value={newSource.listenPort}
                    onChange={(e) => setNewSource({ ...newSource, listenPort: parseInt(e.target.value) })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    min={1}
                    max={65535}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('logCollection:sources.parserType')}
                  </label>
                  <select
                    value={newSource.parserType}
                    onChange={(e) => setNewSource({ ...newSource, parserType: e.target.value as ParserType })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {parserTypes.map((parser) => (
                      <option key={parser.id} value={parser.id}>{parser.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('logCollection:sources.sourceIp')}
                </label>
                <input
                  type="text"
                  value={newSource.sourceIp || ''}
                  onChange={(e) => setNewSource({ ...newSource, sourceIp: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder={t('logCollection:sources.sourceIpPlaceholder')}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleCreateSource}
                disabled={!newSource.name}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {t('common:create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('logCollection:setup.title')}
              </h2>
              <button
                onClick={() => setShowSetupModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Agent Setup Instructions */}
            <div className="space-y-6">
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                  {t('logCollection:setup.agentTitle')}
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  {t('logCollection:setup.agentDescription')}
                </p>
              </div>

              {/* Step 1: Download/Install */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold text-sm">1</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{t('logCollection:setup.step1Title')}</h4>
                </div>
                <div className="ml-11">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {t('logCollection:setup.step1Description')}
                  </p>
                  <div className="bg-gray-900 rounded-lg p-3 text-sm font-mono text-gray-100 overflow-x-auto">
                    <pre className="whitespace-pre-wrap">docker run -d \{'\n'}  --name syslog-agent \{'\n'}  -p 514:514/udp \{'\n'}  -p 1514:1514/tcp \{'\n'}  -e API_URL=https://api-monitoring.devskin.com \{'\n'}  -e API_KEY=YOUR_TENANT_ID \{'\n'}  devskin/syslog-agent</pre>
                  </div>
                </div>
              </div>

              {/* Step 2: Configure Firewall */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold text-sm">2</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{t('logCollection:setup.step2Title')}</h4>
                </div>
                <div className="ml-11 space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('logCollection:setup.step2Description')}
                  </p>

                  {/* Cisco ASA */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Cisco ASA</h5>
                    <div className="bg-gray-900 rounded-lg p-3 text-sm font-mono text-gray-100 overflow-x-auto">
                      <pre className="whitespace-pre-wrap">logging enable{'\n'}logging timestamp{'\n'}logging host inside 192.168.1.100 UDP/514{'\n'}logging trap informational</pre>
                    </div>
                  </div>

                  {/* FortiGate */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">FortiGate</h5>
                    <div className="bg-gray-900 rounded-lg p-3 text-sm font-mono text-gray-100 overflow-x-auto">
                      <pre className="whitespace-pre-wrap">config log syslogd setting{'\n'}  set status enable{'\n'}  set server "192.168.1.100"{'\n'}  set port 514{'\n'}end</pre>
                    </div>
                  </div>

                  {/* Palo Alto */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Palo Alto</h5>
                    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
                      Device → Server Profiles → Syslog → Add server 192.168.1.100:514
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: Verify */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-semibold text-sm">3</span>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{t('logCollection:setup.step3Title')}</h4>
                </div>
                <div className="ml-11">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {t('logCollection:setup.step3Description')}
                  </p>
                  <div className="bg-gray-900 rounded-lg p-3 text-sm font-mono text-gray-100 overflow-x-auto">
                    <pre className="whitespace-pre-wrap"># Test UDP{'\n'}echo "&lt;134&gt;Jan 19 10:00:00 firewall %ASA-6-302013: Test log" | nc -u localhost 514</pre>
                  </div>
                </div>
              </div>

              {/* Supported Vendors */}
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">{t('logCollection:setup.supportedVendors')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { name: 'Cisco ASA/Firepower', icon: '🔒' },
                    { name: 'FortiGate', icon: '🛡️' },
                    { name: 'Palo Alto Networks', icon: '🔥' },
                    { name: 'Check Point', icon: '✓' },
                    { name: 'CEF Format', icon: '📋' },
                    { name: 'Syslog RFC 3164/5424', icon: '📝' },
                  ].map((vendor) => (
                    <div key={vendor.name} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <span>{vendor.icon}</span>
                      <span>{vendor.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowSetupModal(false)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {t('common:close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
